import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Table, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useManagementStore } from "@/stores/management";
import { mcpService, type McpServer } from "@/services/mcp";
import { useCardExtra } from "@/layouts/ManagementLayout";
import { formatDateTime } from "@/utils/date";

export function McpManagement() {
  const { t } = useTranslation("management");
  const { context, hasPermission, permissions } = useManagementStore();
  const orgId = context?.type === "org" ? context.org.id : "";
  const canManage = hasPermission("OrgRoleManage") || context?.type === "org";

  const [mcps, setMcps] = useState<McpServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMcp, setEditingMcp] = useState<McpServer | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      setMcps(await mcpService.list(orgId));
    } catch { setMcps([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [orgId]);

  useCardExtra(
    canManage ? (
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => {
        setEditingMcp(null);
        form.resetFields();
        setModalOpen(true);
      }}>
        {t("mcps.create")}
      </Button>
    ) : null,
    [t, permissions],
  );

  const openEdit = (mcp: McpServer) => {
    setEditingMcp(mcp);
    const headers = mcp.headers && Object.keys(mcp.headers).length > 0
      ? JSON.stringify(mcp.headers, null, 2)
      : "";
    form.setFieldsValue({
      name: mcp.name,
      url: mcp.url,
      headers_json: headers,
      allowed_tools: (mcp.allowed_tools || []).join(", "),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    let headers: Record<string, string> = {};
    if (values.headers_json && values.headers_json.trim()) {
      try {
        headers = JSON.parse(values.headers_json);
      } catch {
        message.error(t("mcps.form.headers_json_invalid"));
        return;
      }
    }
    const payload = {
      name: values.name,
      url: values.url,
      headers,
      allowed_tools: values.allowed_tools
        ? values.allowed_tools.split(",").map((s: string) => s.trim()).filter(Boolean)
        : [],
    };
    setSaving(true);
    try {
      if (editingMcp) {
        await mcpService.update(orgId, editingMcp.id, payload);
      } else {
        await mcpService.create(orgId, payload);
      }
      message.success(t("common:success"));
      setModalOpen(false);
      load();
    } catch (e) { message.error(e instanceof Error ? e.message : t("common:error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await mcpService.delete(orgId, id);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
  };

  const columns = [
    { title: t("mcps.table.name"), dataIndex: "name", key: "name" },
    { title: t("mcps.table.url"), dataIndex: "url", key: "url", ellipsis: true },
    {
      title: t("mcps.table.created_at"), dataIndex: "created_at", key: "created_at",
      render: (v: string) => formatDateTime(v),
    },
    ...(canManage ? [{
      title: t("common:action"), key: "action", width: 100,
      render: (_: unknown, record: McpServer) => (
        <span style={{ display: "flex", gap: 4 }}>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title={t("mcps.delete.confirm")} onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </span>
      ),
    }] : []),
  ];

  return (
    <>
      <Table dataSource={mcps} columns={columns} rowKey="id" loading={loading} size="middle" />
      <Modal
        title={editingMcp ? t("common:edit") : t("mcps.create")}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t("mcps.table.name")} name="name" rules={[{ required: true }]}>
            <Input placeholder="e.g. My MCP Server" />
          </Form.Item>
          <Form.Item label={t("mcps.table.url")} name="url" rules={[{ required: true }]}>
            <Input placeholder="https://mcp.example.com/sse" />
          </Form.Item>
          <Form.Item
            label={t("mcps.form.headers_json")}
            name="headers_json"
            rules={[{
              validator: (_, value) => {
                if (!value || !value.trim()) return Promise.resolve();
                try { JSON.parse(value); return Promise.resolve(); }
                catch { return Promise.reject(new Error(t("mcps.form.headers_json_invalid"))); }
              },
            }]}
          >
            <Input.TextArea
              rows={3}
              placeholder='{"Authorization": "Bearer xxx", "X-Custom": "value"}'
            />
          </Form.Item>
          <Form.Item label={t("mcps.form.allowed_tools")} name="allowed_tools">
            <Input placeholder={t("mcps.form.allowed_tools_hint")} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
